import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';

const GOOGLE_API_KEY = "AIzaSyBAmGdcBdl63l-N4NwkivqFSVfk0cnBrVo";


// This component manages the Google Autocomplete service lifecycle
function AutocompleteInput({ onPlaceSelect, className, placeholder, defaultValue, onInputChange }) {
    const { t } = useTranslation();
    // Get access to the 'places' library using the hook
    const places = useMapsLibrary('places');
    // Create a ref for the HTML input element
    const inputRef = useRef(null);
    // State to hold the Autocomplete instance
    const [placeAutocomplete, setPlaceAutocomplete] = useState(null);

    // State to manage the input's visual value (optional but good practice)
    const [inputValue, setInputValue] = useState(defaultValue || "");

    // Update local state if defaultValue changes (e.g. when parent state updates or on remount)
    useEffect(() => {
        if (defaultValue !== undefined) {
            setInputValue(defaultValue);
        }
    }, [defaultValue]);

    // Refs to hold the latest handlers
    const onPlaceSelectRef = useRef(onPlaceSelect);
    const onInputChangeRef = useRef(onInputChange);

    // Update refs when handlers change
    useEffect(() => {
        onPlaceSelectRef.current = onPlaceSelect;
        onInputChangeRef.current = onInputChange;
    }, [onPlaceSelect, onInputChange]);

    // Initialize the Autocomplete object when the places library is ready
    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options = {
            // Specify which fields to retrieve for the selected place
            fields: ['geometry', 'name', 'formatted_address', 'place_id'],
            // types: ['address', 'establishment'],
            componentRestrictions: { 'country': ['ES'] },
        };

        // Attach Google Autocomplete to the input ref
        const autocomplete = new places.Autocomplete(inputRef.current, options);
        setPlaceAutocomplete(autocomplete);

        // Add a listener to handle when a place is selected
        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();

            // Call the parent handler with the selected place
            if (onPlaceSelectRef.current) {
                onPlaceSelectRef.current(place);
            }

            // Update the input value with the formatted address
            if (place.formatted_address) {
                setInputValue(place.formatted_address);
                if (onInputChangeRef.current) {
                    onInputChangeRef.current(place.formatted_address);
                }
            }
        });

        // Cleanup function for the listener and instance
        return () => {
            // Removing the listener manually is often required for clean up
            if (listener) {
                window.google.maps.event.removeListener(listener);
            }
        };

    }, [places]); // Removed handlers from dependency array to prevent re-initialization

    return (
        <input
            ref={inputRef}
            type="text"
            placeholder={placeholder || t('form.search_placeholder')}
            value={inputValue}
            onChange={(e) => {
                setInputValue(e.target.value);
                if (onInputChange) {
                    onInputChange(e.target.value);
                }
            }}
            className={className || "w-full pl-4 pr-10 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors shadow-sm text-gray-900 placeholder-gray-400"}
        />
    );
}


// The Main Component (GoogleSearch) ---
function GoogleSearch({ onSelect, className, placeholder, defaultValue, onInputChange }) {

    // This function handles the final selection and sends it to the parent component
    const handlePlaceSelect = (place) => {
        // send it up to the parent component
        // console.log("Place Selected:", place);
        onSelect(place);
    };

    return (
        <APIProvider apiKey={GOOGLE_API_KEY} libraries={['places']}>
            <div className="w-full">
                <AutocompleteInput
                    onPlaceSelect={handlePlaceSelect}
                    className={className}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    onInputChange={onInputChange}
                />
            </div>
        </APIProvider>
    );
}

export default GoogleSearch;